import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { X, Download, Loader2, FileText } from "lucide-react";
import { meta } from "@/analytics/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = "/social/guida_checkup_4_visure.pdf";

export default function GuideModal({ open, onClose }) {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const download = () => {
    window.open(PDF_URL, "_blank", "noopener");
    meta.lead({ content_name: "guida_checkup_download" });
    onClose();
  };

  const submit = async () => {
    if (busy) return;
    if (name.trim().length < 2 || phone.trim().length < 6 || !privacy) {
      setErr(t("guide.err"));
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/guide-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(), privacy }),
      });
      if (!res.ok) throw new Error("lead");
      download();
    } catch {
      setErr(t("guide.err"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90] bg-[#003087]/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose} data-testid="guide-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-3xl p-7 sm:p-8 shadow-2xl border border-[#E2E8F0]"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#E6F6FD] flex items-center justify-center shrink-0">
                  <FileText size={20} className="text-[#00AEEF]" />
                </span>
                <h3 className="text-lg font-extrabold text-[#003087] leading-snug">{t("guide.title")}</h3>
              </div>
              <button data-testid="guide-close" onClick={onClose} className="text-[#94A3B8] hover:text-[#003087] p-1 shrink-0" aria-label="chiudi">
                <X size={20} />
              </button>
            </div>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{t("guide.sub")}</p>

            <div className="mt-5 space-y-3">
              <input data-testid="guide-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("guide.name")}
                className="w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:border-[#00AEEF] transition-colors" />
              <input data-testid="guide-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder={t("guide.phone")}
                className="w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:border-[#00AEEF] transition-colors" />
              <label className="flex items-start gap-2.5 text-xs text-[#64748B] cursor-pointer">
                <input data-testid="guide-privacy" type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-0.5 accent-[#00AEEF]" />
                {t("guide.privacy")}
              </label>
              {err && <p data-testid="guide-error" className="text-xs font-semibold text-red-500">{err}</p>}
            </div>

            <button data-testid="guide-submit" onClick={submit} disabled={busy}
              className="btn-green mt-5 w-full rounded-xl py-3.5 px-6 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />} {t("guide.cta")}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#94A3B8]">{t("guide.note")}</p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
